import { css } from '@emotion/core';
import styled from '@emotion/styled';
import { dropdownColors } from '../../../app/colors';
import { layers } from '../../../app/spacing';
import { TextEllipsisWithTooltip } from '../../Kizen/Table';
import { blue, gray, red } from '@kizen/kds/Colors';

const Label = styled.label`
  display: inline-flex;
  align-items: center;
  max-width: 100%;
  margin-bottom: unset; // Undo global label style
  // These two ensure that the height of the parent doesn't
  // change when the box is checked versus unchecked. Just an inline sizing quirk.
  line-height: 0;
  vertical-align: top;
  > input {
    position: absolute;
    opacity: 0;
    z-index: ${layers.content(0)};
    & + * {
      flex-shrink: 0;
      .RadioDot {
        display: none;
      }
    }
    &:checked + * {
      border-color: ${blue['blue-700']};
      background: ${gray['gray-50']};
      .RadioDot {
        display: initial;
      }
    }
    &:disabled + * {
      border-color: ${gray['gray-600']};
      background-color: ${gray['gray-50']};
      .RadioDot {
        background-color: ${gray['gray-600']};
      }
    }
    &:disabled + * + * {
      color: ${gray['gray-600']};
    }
    &:focus-visible + * {
      border-color: ${blue['blue-700']};
      background: ${gray['gray-50']};
    }
    &:focus-visible:checked + * {
      background-color: ${dropdownColors.hover};
    }
  }
  &:hover > input:not(:disabled) + * {
    border-color: ${blue['blue-700']};
    background: ${gray['gray-50']};
  }
  &:hover > input:checked:not(:disabled) + *,
  &:focus-visible > input:checked:not(:disabled) + * {
    background-color: ${dropdownColors.hover};
  }
  ${({ error }) =>
    error &&
    css`
      > input {
        & + *,
        &:checked + * {
          border-color: ${red['red-500']};
        }
      }
      &:hover > input:not(:disabled) + * {
        border-color: ${red['red-500']};
      }
    `}
`;

const VisualRadio = styled.span`
  box-sizing: content-box;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border: 1px solid ${gray['gray-600']};
  background-color: ${gray['gray-50']};
  border-radius: 50%;
  &:not(:last-child) {
    margin-right: 10px;
  }
  width: 15px;
  height: 15px;
  .RadioDot {
    background-color: ${blue['blue-700']};
    border-radius: 50%;
    height: 11px;
    width: 11px;
  }
  ${({ variant }) =>
    variant === 'underline' &&
    css`
      width: 12px;
      height: 12px;
      .RadioDot {
        height: 10px;
        width: 10px;
      }
    `}
`;

export default function Radio({
  label,
  variant,
  error,
  onChange,
  className,
  ...props
}) {
  return (
    <Label className={className} error={error}>
      <input
        type="radio"
        onChange={(ev) => {
          if (onChange) onChange(ev.target.checked, ev);
        }}
        {...props}
      />
      <VisualRadio variant={variant}>
        <div className="RadioDot" />
      </VisualRadio>
      {typeof label !== 'string' && label}
      {typeof label === 'string' && (
        <TextEllipsisWithTooltip
          as="span"
          style={{ height: 'calc(1em + 2px)' }}
        >
          {label}
        </TextEllipsisWithTooltip>
      )}
    </Label>
  );
}
